// frontend-react/src/components/RuleManagement/RuleSetViewPanel.tsx
// 三個 Modal（查看 / AI 生成 / 預覽）共用的主體區塊
import React, { useState } from 'react';
import { Button, Collapse, Tag, Input, Checkbox, Spin } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { RuleRow } from '../../types/rule';
import RuleSetHeader from './RuleSetHeader';
import RulePreviewTable from './RulePreviewTable';

const RISK_LEVELS_ORDER = ['high', 'medium', 'low'] as const;
const RISK_LABELS: Record<string, { label: string; color: string }> = {
  high: { label: '高風險', color: 'red' },
  medium: { label: '中風險', color: 'orange' },
  low: { label: '低風險', color: 'green' },
};

interface RuleSetViewPanelProps {
  // 標題區
  name: string;
  nameEditable?: boolean;
  onNameChange?: (name: string) => void;
  subtext?: string | undefined; // 例：「上傳者：xxx (部門)」
  loading?: boolean;

  // 規則表格
  rules: RuleRow[];
  expandedRows: string[];
  onExpandChange: (keys: string[]) => void;
  editMode?: boolean; // 是否顯示編輯欄，預設 true
  editingRowKey: string | null;
  savingRow: boolean;
  onEditRow: (key: string) => void;
  onCancelEdit: () => void;
  onSaveRow: (key: string, name: string, content: string) => void;
  onDeleteRow: (key: string) => void;

  // 風險等級（選填）
  showRisk?: boolean;
  riskLevels?: Record<string, string>;
  defaultRiskLevels?: Record<string, string>;
  canEditRisk?: boolean;
  onSaveRiskLevels?: (
    levels: Record<string, string> | null
  ) => Promise<{ success: boolean; error?: string }>;
}

const RuleSetViewPanel: React.FC<RuleSetViewPanelProps> = ({
  name,
  nameEditable = false,
  onNameChange,
  subtext,
  loading = false,
  rules,
  expandedRows,
  onExpandChange,
  editMode = true,
  editingRowKey,
  savingRow,
  onEditRow,
  onCancelEdit,
  onSaveRow,
  onDeleteRow,
  showRisk = false,
  riskLevels = {},
  defaultRiskLevels = {},
  canEditRisk = false,
  onSaveRiskLevels,
}) => {
  const [riskIsEditing, setRiskIsEditing] = useState(false);
  const [riskCollapseOpen, setRiskCollapseOpen] = useState(false);
  const [riskUseDefault, setRiskUseDefault] = useState(false);
  const [draftRisk, setDraftRisk] = useState<Record<string, string>>({});
  const [riskSaving, setRiskSaving] = useState(false);

  const hasRiskData = showRisk && Object.keys(riskLevels).length > 0;

  const isRiskCustom = RISK_LEVELS_ORDER.some(
    k => (riskLevels[k] || '') !== (defaultRiskLevels[k] || '')
  );

  const handleRiskEdit = () => {
    setDraftRisk({ ...riskLevels });
    setRiskUseDefault(false);
    setRiskIsEditing(true);
    setRiskCollapseOpen(true);
  };

  const handleRiskCancel = () => setRiskIsEditing(false);

  const handleRiskSave = async () => {
    if (!onSaveRiskLevels) return;
    setRiskSaving(true);
    const result = await onSaveRiskLevels(riskUseDefault ? null : draftRisk);
    setRiskSaving(false);
    if (result.success) setRiskIsEditing(false);
  };

  const renderRiskReadOnly = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {RISK_LEVELS_ORDER.map(level => {
        const value = riskLevels[level];
        if (!value) return null;
        const { label, color } = RISK_LABELS[level] ?? { label: level, color: 'default' };
        return (
          <div
            key={level}
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: '6px',
              padding: '8px 12px',
            }}
          >
            <Tag color={color} style={{ flexShrink: 0, marginTop: '1px', fontWeight: 500 }}>
              {label}
            </Tag>
            <span style={{ fontSize: '13px', color: '#444', lineHeight: '1.6' }}>{value}</span>
          </div>
        );
      })}
    </div>
  );

  const renderRiskEdit = () => (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
          flexWrap: 'wrap',
        }}
      >
        <Checkbox
          checked={riskUseDefault}
          onChange={e => {
            setRiskUseDefault(e.target.checked);
            if (e.target.checked) setDraftRisk({ ...defaultRiskLevels });
          }}
          style={{ marginLeft: '8px' }}
        >
          預設
        </Checkbox>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <Button size='small' onClick={handleRiskCancel} disabled={riskSaving}>
            取消
          </Button>
          <Button type='primary' size='small' onClick={handleRiskSave} loading={riskSaving}>
            儲存
          </Button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {RISK_LEVELS_ORDER.map(level => {
          const { label, color } = RISK_LABELS[level] ?? { label: level, color: 'default' };
          return (
            <div key={level} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <Tag
                color={color}
                style={{
                  flexShrink: 0,
                  marginTop: '6px',
                  fontWeight: 500,
                  minWidth: '52px',
                  textAlign: 'center',
                }}
              >
                {label}
              </Tag>
              <Input.TextArea
                value={draftRisk[level] ?? ''}
                onChange={e => setDraftRisk(prev => ({ ...prev, [level]: e.target.value }))}
                disabled={riskUseDefault}
                autoSize={{ minRows: 2 }}
                style={{
                  flex: 1,
                  fontSize: '13px',
                  background: riskUseDefault ? '#f5f5f5' : '#fff',
                  color: riskUseDefault ? '#888' : '#222',
                }}
              />
            </div>
          );
        })}
      </div>
      {riskUseDefault && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
          勾選後將還原為公司統一設定的預設內容，日後 Admin 修改公司預設時，此規則集會自動跟著更新。
        </div>
      )}
    </>
  );

  return (
    <Spin spinning={loading} tip='載入規則中...'>
      {/* ── 名稱 + 操作列 ── */}
      <div style={{ padding: '0 12px 8px' }}>
        <RuleSetHeader name={name} editable={nameEditable} onNameChange={onNameChange} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', color: '#888' }}>{subtext ?? ''}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              type='link'
              size='small'
              style={{ padding: '0 4px', fontSize: '13px' }}
              disabled={rules.length === 0}
              onClick={() => {
                if (expandedRows.length === rules.length) {
                  onExpandChange([]);
                } else {
                  onExpandChange(rules.map(r => r.key));
                }
              }}
            >
              {expandedRows.length === rules.length ? '▲ 全部收合' : '▼ 全部展開'}
            </Button>
            <span
              style={{
                background: '#1E88A8',
                color: '#fff',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              共 {rules.length} 條規則
            </span>
          </div>
        </div>
      </div>

      {/* ── 規則表格 ── */}
      <div style={{ padding: '0 12px' }}>
        <RulePreviewTable
          rules={rules}
          expandedRows={expandedRows}
          onExpandChange={onExpandChange}
          showExpandAll={false}
          editMode={editMode}
          editingRowKey={editingRowKey}
          savingRow={savingRow}
          onEditRow={onEditRow}
          onCancelEdit={onCancelEdit}
          onSaveRow={onSaveRow}
          onDeleteRow={onDeleteRow}
        />

        {/* ── 風險等級 ── */}
        {hasRiskData && (
          <Collapse
            size='small'
            style={{ marginTop: '16px' }}
            activeKey={riskCollapseOpen ? ['risk'] : []}
            onChange={keys => setRiskCollapseOpen((keys as string[]).indexOf('risk') !== -1)}
            items={[
              {
                key: 'risk',
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#333' }}>風險等級定義</span>
                    <Tag
                      color={
                        riskIsEditing
                          ? riskUseDefault
                            ? 'blue'
                            : 'orange'
                          : isRiskCustom
                            ? 'orange'
                            : 'blue'
                      }
                      style={{ margin: 0 }}
                    >
                      {riskIsEditing
                        ? riskUseDefault
                          ? '預設'
                          : '自訂'
                        : isRiskCustom
                          ? '自訂'
                          : '預設'}
                    </Tag>
                  </span>
                ),
                extra:
                  canEditRisk && !riskIsEditing ? (
                    <Button
                      type='link'
                      size='small'
                      icon={<EditOutlined />}
                      style={{ padding: 0, height: 'auto', fontSize: '13px' }}
                      onClick={e => {
                        e.stopPropagation();
                        handleRiskEdit();
                      }}
                    ></Button>
                  ) : undefined,
                children: riskIsEditing ? renderRiskEdit() : renderRiskReadOnly(),
              },
            ]}
          />
        )}
      </div>
    </Spin>
  );
};

export default RuleSetViewPanel;
